/**
 * MyTicketsPage - the customer's ticket list, mirroring the agent
 * inbox table (SPEC §8 + Greg's 2026-05-17 brief).
 *
 * Layout:
 *  - Breadcrumb + icon header + h1 + "New ticket" button (right).
 *  - Status chips row above the table (clicking a chip drives ``?status=``
 *    on the URL; persists across reloads).
 *  - Scope toggle (Mine / All company tickets) when the customer has
 *    company-scope permission. Server silently downgrades to ``mine``
 *    when the permission flag is off.
 *  - Filter dropdowns: Priority + Type. NO Assignee filter (customers
 *    do not see an assignee column).
 *  - Table: # (public_code) / Status / Priority / Contact / Subject /
 *    Updated. Sortable column headers. Drops the agent table's Company
 *    and Assignee columns.
 *
 * State:
 *  - status in URL ?status= (back-button preserves filter).
 *  - scope in localStorage (per-browser; carried over from the pre-
 *    table version of this page).
 *  - priority / type / sort kept as component state for now; URL +
 *    localStorage persistence can come back if it turns out customers
 *    want it. The agent inbox uses server-side preferences which is
 *    heavier than this surface needs.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Select, Spinner } from "@/components/ui";
import { useClearSearchSignal } from "@/hooks/useClearSearchSignal";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  useMyTickets,
  type CustomerListSortBy,
  type SortOrder,
  type TicketsScope,
} from "@/hooks/useMyTickets";
import { useAuthStore } from "@/stores/auth";
import type {
  TicketPriority,
  TicketStatus,
  TicketSummary,
  TicketType,
} from "@/types/api";

import { PriorityBadge } from "@/components/tickets/PriorityBadge";
import { StatusBadge } from "@/components/tickets/StatusBadge";

const SCOPE_STORAGE_KEY = "buyist-support:my-tickets-scope";

// Sentinel for the "All Open Tickets" preset (Greg 2026-06-18). Not a
// TicketStatus, so it lives as a distinct string in the same Status
// <Select> (mirrors the agent inbox's __mine_or_unassigned__ pattern).
// When active the hook sends ``view=all_open`` instead of ``status=``.
const ALL_OPEN_SENTINEL = "__all_open__";

// Status filter options used by the Select dropdown above the table.
// Matches the agent inbox's STATUS_OPTIONS shape (a value of "" reads
// as "All" - the agent inbox does the same so customers and agents
// share the same dropdown grammar). The value type is ``string`` (not
// ``TicketStatus | ""``) so the preset sentinel can ride in the same
// dropdown, exactly as the agent inbox hosts its view sentinels.
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: ALL_OPEN_SENTINEL, label: "All Open Tickets" },
  { value: "", label: "All statuses" },
  { value: "open", label: "Open" },
  { value: "pending", label: "Pending" },
  { value: "waiting_customer", label: "Waiting on Customer" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
  { value: "reopened", label: "Reopened" },
];

// Customer-visible priority + type vocabularies. Match the SPEC §8
// NewTicketPage restrictions: customers can see Urgent on imported
// tickets but cannot select it themselves; the filter dropdown
// surfaces it so they can still filter to a ticket the agent flagged.
const PRIORITY_OPTIONS: { value: TicketPriority | ""; label: string }[] = [
  { value: "", label: "All priorities" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

// Customer-visible types only, sorted alphabetically; "All types" first.
const TYPE_OPTIONS: { value: TicketType | ""; label: string }[] = [
  { value: "", label: "All types" },
  { value: "Problem", label: "Problem" },
  { value: "Question", label: "Question" },
  { value: "Service Request", label: "Service Request" },
];

function readPersistedScope(): TicketsScope {
  try {
    const v = localStorage.getItem(SCOPE_STORAGE_KEY);
    if (v === "company" || v === "mine") return v;
  } catch {
    /* noop */
  }
  return "mine";
}

function writePersistedScope(scope: TicketsScope): void {
  try {
    localStorage.setItem(SCOPE_STORAGE_KEY, scope);
  } catch {
    /* noop */
  }
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

interface SortableThProps {
  column: CustomerListSortBy;
  label: string;
  sortBy: CustomerListSortBy;
  sortOrder: SortOrder;
  onToggle: (col: CustomerListSortBy) => void;
}

function SortableTh({
  column,
  label,
  sortBy,
  sortOrder,
  onToggle,
}: SortableThProps): JSX.Element {
  const isActive = sortBy === column;
  const arrow = !isActive ? "" : sortOrder === "desc" ? " ▼" : " ▲";
  return (
    <th className="px-3 py-2 text-left">
      <button
        type="button"
        onClick={() => onToggle(column)}
        className={`flex items-center gap-1 text-xs font-medium uppercase tracking-wider ${
          isActive
            ? "text-primary-700"
            : "text-neutral-500 hover:text-neutral-700"
        }`}
        data-testid={`sort-th-${column}`}
      >
        {label}
        {arrow && <span aria-hidden="true">{arrow}</span>}
      </button>
    </th>
  );
}

function TicketTableRow({ ticket }: { ticket: TicketSummary }): JSX.Element {
  const navigate = useNavigate();
  return (
    <tr
      onClick={() => navigate(`/tickets/${ticket.public_code.toLowerCase()}`)}
      className="cursor-pointer hover:bg-neutral-50"
    >
      <td className="whitespace-nowrap px-3 py-3 text-xs font-medium text-neutral-700">
        <span data-testid="ticket-number">{ticket.public_code}</span>
      </td>
      <td className="px-3 py-3">
        <StatusBadge status={ticket.status} viewer="customer" />
      </td>
      <td className="px-3 py-3">
        <PriorityBadge priority={ticket.priority} />
      </td>
      <td className="px-3 py-3 text-sm text-neutral-700">
        {ticket.requester_name || ticket.requester_email}
      </td>
      <td className="px-3 py-3 text-sm text-neutral-900">
        <span className="line-clamp-2">{ticket.subject}</span>
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-xs text-neutral-500">
        {formatRelative(ticket.last_activity_at ?? ticket.updated_at)}
      </td>
    </tr>
  );
}

export function MyTicketsPage(): JSX.Element {
  usePageTitle("My Tickets - Buyist Support");

  const user = useAuthStore((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get("status");
  const activeStatus: TicketStatus | null = useMemo(() => {
    const allowed = STATUS_OPTIONS.map((f) => f.value).filter(
      (v): v is TicketStatus => v !== "" && v !== ALL_OPEN_SENTINEL,
    );
    return (allowed.find((v) => v === statusParam) ?? null) as TicketStatus | null;
  }, [statusParam]);
  // The "All Open Tickets" preset rides in ?status= as a sentinel; it is
  // NOT a TicketStatus, so activeStatus stays null while it is active and
  // the hook sends ``view=all_open`` instead.
  const isAllOpen = statusParam === ALL_OPEN_SENTINEL;
  const hasStatusFilter = activeStatus !== null || isAllOpen;

  // Permission flag for the Mine / Company scope toggle. See the TODO
  // in the previous version of this file - skinny auth-store shape
  // doesn't carry the permission row yet; server silently downgrades
  // when the flag is off.
  const canMaybeSeeCompanyScope = user?.role === "customer";
  const [scope, setScope] = useState<TicketsScope>(() => readPersistedScope());
  useEffect(() => {
    writePersistedScope(scope);
  }, [scope]);
  const effectiveScope: TicketsScope = canMaybeSeeCompanyScope
    ? scope
    : "mine";

  // Additional filters (transient component state - not persisted).
  const [priority, setPriority] = useState<TicketPriority | "">("");
  const [type, setType] = useState<TicketType | "">("");

  // Free-text search. Debounced 300 ms (mirrors the agent inbox) so we
  // do not fire a request per keystroke. ``rawQ`` drives the input;
  // ``debouncedQ`` (trimmed) drives the query. Blank = no search.
  const [rawQ, setRawQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(rawQ), 300);
    return () => clearTimeout(t);
  }, [rawQ]);
  const trimmedQ = debouncedQ.trim();
  const hasSearch = trimmedQ.length > 0;

  // Same contract as the agent inbox: the sidebar Tickets item clears
  // the search box, browser Back does not. ``debouncedQ`` is set here
  // too so the list swaps back without waiting out the debounce.
  useClearSearchSignal(() => {
    setRawQ("");
    setDebouncedQ("");
  });

  // Sort state. Defaults match the agent inbox - last activity first.
  const [sortBy, setSortBy] = useState<CustomerListSortBy>("last_activity_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  function toggleSort(column: CustomerListSortBy): void {
    if (column === sortBy) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
  }

  const { data, isLoading, isError, error } = useMyTickets({
    status: isAllOpen ? undefined : (activeStatus ?? undefined),
    view: isAllOpen ? "all_open" : undefined,
    priority: priority || undefined,
    type: type || undefined,
    scope: effectiveScope,
    q: trimmedQ || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  function selectStatus(value: string): void {
    const next = new URLSearchParams(searchParams);
    if (value === "") {
      next.delete("status");
    } else {
      next.set("status", value);
    }
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="w-full">
      {/* Header. The pre-2026-05-18 "Dashboard > My Tickets"
          breadcrumb was removed when Dashboard was pulled for launch;
          My Tickets is now the landing page so there is no parent
          to walk back to. */}
      <div className="mb-6 flex items-center gap-3">
        <i
          className="fa-duotone fa-thin fa-ticket text-3xl text-primary-600"
          aria-hidden="true"
        />
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">
            My Tickets
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            Track your support tickets, reply, or reopen tickets that
            weren't fully resolved.
          </p>
        </div>
        <div className="ml-auto">
          <Link
            to="/tickets/new"
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700"
          >
            <i
              className="fa-duotone fa-thin fa-plus text-base"
              aria-hidden="true"
            />
            New ticket
          </Link>
        </div>
      </div>

      {/* Filter bar - Search + Status / Priority / Type dropdowns
          mirroring the agent inbox filter row (without the Assignee
          filter, per Greg's brief). Scope toggle pinned to the right. */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        {/* Free-text search. Matches the subject, description, public
            replies, and attachment names of the tickets in the current
            scope (with company scope + permission, the whole org). */}
        <div className="relative w-64">
          <i
            className="fa-duotone fa-thin fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={rawQ}
            onChange={(e) => setRawQ(e.target.value)}
            maxLength={200}
            placeholder="Search tickets"
            aria-label="Search tickets"
            className="w-full rounded-lg border border-neutral-300 bg-white py-2 pl-9 pr-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <div className="w-44">
          <Select
            id="customer-status-filter"
            options={STATUS_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            value={isAllOpen ? ALL_OPEN_SENTINEL : (activeStatus ?? "")}
            onChange={(e) => selectStatus(e.target.value)}
            aria-label="Status"
          />
        </div>
        <div className="w-40">
          <Select
            id="customer-priority-filter"
            options={PRIORITY_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            value={priority}
            onChange={(e) => setPriority(e.target.value as TicketPriority | "")}
            aria-label="Priority"
          />
        </div>
        <div className="w-48">
          <Select
            id="customer-type-filter"
            options={TYPE_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            value={type}
            onChange={(e) => setType(e.target.value as TicketType | "")}
            aria-label="Type"
          />
        </div>

        {/* Scope toggle (Mine / Company) */}
        {canMaybeSeeCompanyScope && (
          <div
            className="ml-auto inline-flex rounded-full bg-neutral-100 p-1"
            role="tablist"
            aria-label="Ticket scope"
          >
            {(
              [
                { v: "mine", label: "My tickets" },
                { v: "company", label: "All company tickets" },
              ] as const
            ).map((opt) => {
              const active = scope === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setScope(opt.v)}
                  className={`rounded-full px-4 py-1 text-xs font-medium transition-colors ${
                    active
                      ? "bg-white text-neutral-900 shadow-sm"
                      : "text-neutral-600 hover:text-neutral-800"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
        {isLoading && (
          <div className="flex items-center justify-center px-4 py-12">
            <Spinner />
          </div>
        )}

        {isError && (
          <div className="px-4 py-8 text-center text-sm text-red-700">
            {error instanceof Error
              ? error.message
              : "Failed to load tickets."}
          </div>
        )}

        {!isLoading && !isError && (data?.length ?? 0) === 0 && (
          <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
            <i
              className={`fa-duotone fa-thin text-4xl text-neutral-300 ${
                hasSearch ? "fa-magnifying-glass" : "fa-life-ring"
              }`}
              aria-hidden="true"
            />
            <p className="mt-2 text-sm font-medium text-neutral-700">
              {hasSearch
                ? "No tickets match your search"
                : !hasStatusFilter
                  ? "No tickets yet"
                  : "No tickets match this filter"}
            </p>
            <p className="mt-1 text-xs text-neutral-500">
              {hasSearch
                ? "Try different words or clear the search."
                : !hasStatusFilter
                  ? "Open a new ticket to get started."
                  : "Try a different status filter."}
            </p>
          </div>
        )}

        {!isLoading && !isError && (data?.length ?? 0) > 0 && (
          <table className="w-full divide-y divide-neutral-100">
            <thead className="bg-neutral-50">
              <tr>
                <SortableTh
                  column="ticket_number"
                  label="#"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onToggle={toggleSort}
                />
                <SortableTh
                  column="status"
                  label="Status"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onToggle={toggleSort}
                />
                <SortableTh
                  column="priority"
                  label="Priority"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onToggle={toggleSort}
                />
                <SortableTh
                  column="requester_name"
                  label="Contact"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onToggle={toggleSort}
                />
                <SortableTh
                  column="subject"
                  label="Subject"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onToggle={toggleSort}
                />
                <SortableTh
                  column="last_activity_at"
                  label="Updated"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onToggle={toggleSort}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 bg-white">
              {data?.map((t) => <TicketTableRow key={t.id} ticket={t} />)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default MyTicketsPage;
